import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppLoading } from '@/stores/app/loading';
import {
  getRequisition,
  getJobPosts,
  getHiringTeam,
  getReqCandidates,
  fetchActiveCandidatesCSV,
} from '@/api/requisitions';
import { cloneDeep, startCase, kebabCase } from 'lodash-es';
import { arrayToCSVDownload, handleCSVDownload } from '@/helpers/fileDownload';
import { format } from 'date-fns';
import { READABLE_FILE_NAME_FORMAT } from '@/helpers/date';

const defaultActiveCandidates = {
  screen: {
    lastFetchedPage: 0,
    lastSortedBy: '-applied_at',
    totalPages: 0,
    totalItems: 0,
    items: [],
  },
  assessment: {
    lastFetchedPage: 0,
    lastSortedBy: '-applied_at',
    totalPages: 0,
    totalItems: 0,
    items: [],
  },
  offer: {
    lastFetchedPage: 0,
    lastSortedBy: '-applied_at',
    totalPages: 0,
    totalItems: 0,
    items: [],
  },
};

const tableKeys = ['active', 'advanced', 'rejected', 'dropped_out'];

export const useRequisitionReport = defineStore('requisitionReport', () => {
  // Other Stores
  const { addErrorSnackbar } = useAppSnackbar();
  const { setLoading } = useAppLoading();

  // State
  const reqUuid = ref(null);

  const title = ref('');
  const reqId = ref(null);
  const createdAt = ref(null);
  const openedAt = ref(null);
  const closedAt = ref(null);
  const status = ref('');
  const onHold = ref(false);

  const posts = ref([]);
  const postsPage = ref(1);
  const postsTotalPages = ref(1);

  const hiringTeam = ref([]);
  const hiringTeamPage = ref(1);
  const hiringTeamTotalPages = ref(1);
  const hiringTeamSort = ref('-team');

  const shareHiringTeam = ref([]);

  const applicants = ref({});
  const gender = ref({});
  const source = ref({});
  const table = ref({});

  const activeCandidates = ref(cloneDeep(defaultActiveCandidates));
  const candidatesLoading = ref(false);
  const candidatesLoadingMore = ref(false);
  const candidatesSort = ref('-applied_at');
  const selectedCandidateStage = ref(null);

  const applicantWidgetUnit = ref('week');

  const authorized = ref(false);

  const emailStatus = ref({
    succeeded: {
      type: 'success',
      message: 'One or more emails have been sent successfully',
      tooltipMessage: 'Emails sent to:',
      items: [],
    },
    failed: {
      type: 'error',
      message: 'One or more emails failed to send',
      tooltipMessage: 'Failed to send to:',
      items: [],
    },
  });

  // Computed
  const hiringTeamSortDirection = computed(() => {
    if (hiringTeamSort.value.startsWith('-')) return 'desc';
    return 'asc';
  });

  const candidatesSortDirection = computed(() => {
    if (candidatesSort.value.startsWith('-')) return 'desc';
    return 'asc';
  });

  const noDataForExport = computed(() => (
    (!applicants.value || Object.keys(applicants.value).length === 0)
      && (!gender.value || Object.keys(gender.value).length === 0)
      && (!source.value || Object.keys(source.value).length === 0)
      && (!table.value || Object.keys(table.value).length === 0)
      && Object.values(activeCandidates.value).every(
        (stage) => !stage.items
          || stage.items.length === 0,
      )
  ));

  const tableTotalRow = computed(() => {
    const totals = {};

    // Build totals
    Object.keys(table.value).forEach((key) => {
      tableKeys.forEach((k) => {
        if (key !== 'hired' && typeof table.value?.[key]?.[k] === 'number') {
          if (!totals[key]) totals[key] = table.value[key][k];
          else { totals[key] += table.value[key][k]; }
        } else if (key === 'hired' && typeof table.value?.[key] === 'number') {
          if (!totals[key]) totals[key] = table.value[key];
        }
      });
    });

    const keysTable = Object.keys(table.value);

    // Add in percentages
    for (let i = 0; i < keysTable.length; i++) {
      const key = keysTable[i];
      if (i < keysTable.length - 1) {
        totals[key] = {
          value: totals[key],
          percentage: totals[keysTable[i + 1]]
            ? totals[keysTable[i + 1]] / totals[key]
            : 0,
        };
      } else {
        totals[key] = {
          value: totals[key],
          percentage: null,
        };
      }
    }

    totals.isParent = true;
    totals.label = 'Total';

    return totals;
  });

  const tableData = computed(() => {
    const items = [];

    tableKeys.forEach((key) => {
      const item = {
        label: startCase(key),
      };

      Object.keys(table.value).forEach((k) => {
        if (k !== 'hired') {
          item[k] = { value: table.value?.[k]?.[key], percentage: null };
        }
      });

      item.parent = true;

      items.push(item);
    });

    return [tableTotalRow.value, ...items];
  });

  // Mutations
  const updateReqUuid = (newUuid) => {
    reqUuid.value = newUuid;
  };

  const updateInfo = ({
    requisition_title: newTitle,
    ats_external_requisition_id: newReqId,
    created_at: newCreatedAt,
    opened_at: newOpenedAt,
    closed_at: newClosedAt,
    status: newStatus,
    on_hold: newOnHold,
  }) => {
    title.value = newTitle;
    reqId.value = newReqId;
    createdAt.value = newCreatedAt;
    openedAt.value = newOpenedAt;
    closedAt.value = newClosedAt;
    status.value = newStatus;
    onHold.value = newOnHold;
  };

  const updatePosts = ({
    items: newPosts,
    page: newPage,
    pages: newTotalPages,
  }) => {
    posts.value = newPosts;
    postsPage.value = newPage;
    postsTotalPages.value = newTotalPages;
  };

  const updateHiringTeam = ({
    items: newHiringTeam,
    pagination,
    sorting = {},
  }) => {
    hiringTeam.value = newHiringTeam;
    hiringTeamPage.value = pagination?.page || 1;
    hiringTeamTotalPages.value = pagination?.totalPages || 1;
    hiringTeamSort.value = sorting.value || '';
  };

  const updateApplicants = ({
    this_week_count: lastWeek,
    previous_week_count: previousWeek,
    this_month_count: lastMonth,
    previous_month_count: previousMonth,
    this_quarter_count: lastQuarter,
    previous_quarter_count: previousQuarter,
  }) => {
    applicants.value = {
      lastWeek,
      previousWeek,
      lastMonth,
      previousMonth,
      lastQuarter,
      previousQuarter,
    };
  };

  const updateRequisitionReport = ({
    info,
    posts: newPosts,
    hiringTeam: newHiringTeam,
    applicants: newApplicants,
    gender: newGender,
    source: newSource,
    table: newTable,
    authorized: newAuthorized = false,
  }) => {
    updateInfo(info);
    updatePosts(newPosts);
    updateHiringTeam(newHiringTeam);
    updateApplicants(newApplicants);
    gender.value = newGender;
    source.value = newSource;
    table.value = newTable;
    authorized.value = newAuthorized;
  };

  const updateEmails = ({ succeeded, failed }) => {
    emailStatus.value.failed = {
      ...emailStatus.value.failed,
      items: failed,
    };
    emailStatus.value.succeeded = {
      ...emailStatus.value.succeeded,
      items: succeeded,
    };
  };

  const updateActiveCandidates = (
    stage,
    {
      totalPages, page, items, totalItems, sort,
    },
  ) => {
    activeCandidates.value[stage] = {
      lastFetchedPage: page,
      totalPages,
      totalItems,
      items,
      lastSortedBy: sort,
    };
  };

  const updateCandidatesSorting = (sorting = '-applied_at') => {
    candidatesSort.value = sorting;
  };

  const resetActiveCandidates = () => {
    activeCandidates.value = cloneDeep(defaultActiveCandidates);
  };

  const resetSorting = () => {
    hiringTeamSort.value = '-team';
    candidatesSort.value = '-applied_at';
  };

  const resetReqReportData = () => {
    resetActiveCandidates();
    resetSorting();
  };

  const updateApplicantWidgetUnit = (unit) => {
    applicantWidgetUnit.value = unit;
  };

  const updateActiveCandidatesStage = (stage) => {
    selectedCandidateStage.value = stage;
  };

  // Actions
  const getRequisitionReport = async (requisitionId) => {
    setLoading({ pageLoading: true });

    try {
      const requisition = await getRequisition(
        requisitionId,
        { hiringTeamSort: hiringTeamSort.value },
      );

      updateRequisitionReport(requisition);
    } catch (error) {
      addErrorSnackbar({ text: `Get requisition - ${error.message}` });
      throw error;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const getShareUsersWithAccess = async (requisitionId) => {
    try {
      const users = await getHiringTeam({
        page: 1,
        reqId: requisitionId,
        sort: '-team',
        pageSize: 100,
      });

      shareHiringTeam.value = users.items;
    } catch (error) {
      addErrorSnackbar({ text: `Get share users - ${error.message}` });
      throw error;
    }
  };

  const getActiveCandidatesByStage = async (stage, sort = candidatesSort.value) => {
    const {
      lastFetchedPage,
      lastSortedBy,
    } = activeCandidates.value[stage];

    if (lastFetchedPage > 0 && sort === lastSortedBy) return;

    candidatesLoading.value = true;

    try {
      const { items, pagination, sorting } = await getReqCandidates({
        page: 1, reqId: reqUuid.value, stage, sort,
      });

      updateActiveCandidates(
        stage,
        {
          totalPages: pagination.totalPages,
          page: pagination.page,
          items,
          totalItems: pagination.totalItems,
          sort: sorting.value,
        },
      );
      updateCandidatesSorting(sorting.value);
    } catch (error) {
      addErrorSnackbar({ text: `Get requisition candidates - ${error.message}` });
      throw error;
    } finally {
      candidatesLoading.value = false;
    }
  };

  const loadMoreActiveCandidatesByStage = async (stage, sort = candidatesSort.value) => {
    const { lastFetchedPage, totalItems, items: currentStageItems } = activeCandidates.value[stage];

    if (currentStageItems.length >= totalItems) return;

    candidatesLoadingMore.value = true;

    try {
      const {
        items,
        pagination,
        sorting,
      } = await getReqCandidates({
        page: lastFetchedPage + 1, reqId: reqUuid.value, stage, sort,
      });

      updateActiveCandidates(stage, {
        totalPages: pagination.totalPages,
        page: pagination.page,
        totalItems: pagination.totalItems,
        items: [...currentStageItems, ...items],
        sort: sorting.value,
      });
      updateCandidatesSorting(sorting.value);
    } catch (error) {
      addErrorSnackbar({ text: `Load more active candidates - ${error.message}` });
      throw error;
    } finally {
      candidatesLoadingMore.value = false;
    }
  };

  const updatePostsPagination = async (page) => {
    setLoading({ pageLoading: true });

    try {
      const postsResp = await getJobPosts(page, reqUuid.value);

      postsPage.value = postsResp.page;
      postsTotalPages.value = postsResp.pages;
      posts.value = postsResp.items;
    } catch (error) {
      addErrorSnackbar({ text: `Update requisition Job Posts - ${error.message}` });
      throw error;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const updateHiringTeamData = async ({
    page = 1,
    sort = hiringTeamSort.value,
  }) => {
    setLoading({ pageLoading: true });

    try {
      const hiringTeamResp = await getHiringTeam({
        page,
        reqId: reqUuid.value,
        sort,
        excludeSharedRoles: true,
      });

      hiringTeamPage.value = hiringTeamResp.pagination.page;
      hiringTeamTotalPages.value = hiringTeamResp.pagination.totalPages;
      hiringTeam.value = hiringTeamResp.items;
      hiringTeamSort.value = hiringTeamResp.sorting.value;
    } catch (error) {
      addErrorSnackbar({ text: `Update requisition Hiring Team - ${error.message}` });
      throw error;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const downloadApplicantCountCSV = (projectName) => {
    const rows = [
      ['', 'Application', 'Screen', 'Assessment', 'Offer', 'Hire'],
      ...tableData.value.map((row) => [
        row.label,
        row.application?.value,
        row.screen?.value,
        row.assessment?.value,
        row.offer?.value,
        row.hired?.value,
      ]),
    ];

    const today = format(new Date(), READABLE_FILE_NAME_FORMAT);

    arrayToCSVDownload({
      array: rows,
      title: `${kebabCase(projectName.toLowerCase())}-requisition-report-applicants-table-${today}.csv`,
      autoClick: true,
    });
  };

  const downloadActiveCandidatesCSV = async (selectedStages, projectName) => {
    const commaSeparatedString = await fetchActiveCandidatesCSV({
      reqId: reqUuid.value,
      sort: candidatesSort.value,
      selectedStages,
    });

    const today = format(new Date(), READABLE_FILE_NAME_FORMAT);

    handleCSVDownload({
      commaSeparatedString,
      autoClick: true,
      title: `${kebabCase(projectName.toLowerCase())}-requisition-report-active-candidates-${today}.csv`,
    });
  };

  const updateViaQueryParams = (query) => {
    if (query?.recent_apps) applicantWidgetUnit.value = query.recent_apps;
    if (query?.active_candidate_stage) selectedCandidateStage.value = query.active_candidate_stage;
    if (query?.active_candidates_sort) candidatesSort.value = query.active_candidates_sort;
  };

  return {
    // State
    reqUuid,

    reqId,
    title,
    createdAt,
    openedAt,
    closedAt,
    status,
    onHold,

    posts,
    postsPage,
    postsTotalPages,

    hiringTeam,
    hiringTeamPage,
    hiringTeamTotalPages,
    hiringTeamSort,

    shareHiringTeam,

    applicants,
    gender,
    source,
    table,
    authorized,
    emailStatus,

    activeCandidates,
    candidatesLoading,
    candidatesLoadingMore,
    candidatesSort,
    selectedCandidateStage,

    applicantWidgetUnit,

    // Computed
    hiringTeamSortDirection,
    candidatesSortDirection,
    noDataForExport,

    tableTotalRow,
    tableData,

    // Mutations
    updateReqUuid,
    updateRequisitionReport,
    updateEmails,
    updatePosts,
    updateInfo,
    updateHiringTeam,
    updateApplicants,
    updateCandidatesSorting,
    resetReqReportData,
    updateApplicantWidgetUnit,
    updateActiveCandidatesStage,

    // Actions
    getRequisitionReport,
    getShareUsersWithAccess,
    getActiveCandidatesByStage,
    loadMoreActiveCandidatesByStage,
    updatePostsPagination,
    updateHiringTeamData,
    downloadApplicantCountCSV,
    downloadActiveCandidatesCSV,
    updateViaQueryParams,
  };
});
